// Andon: every open andon, as the fourth of the five places (frame v2.12,
// script L939 and L954).
//
// TWO SOURCES, ONE SCREEN. An operator sees the andons at their station, from
// the shop floor's own read, in ONE group — "Andons · At your work centre —
// reply in the thread" — with Reply on every card, because the operator's
// list is a place to read and to walk from: the one act open to them from the
// list is joining the conversation. A leader on the floor sees every open
// andon on it from the andon module's floor read, as the Andon app's own
// cards, grouped NOBODY YET then RESPONDING (the spec's amendment to the
// frame's one list), with "Take it" on the face of an unowned one opening the
// ETA sheet right here. Under the andons the leader also gets the STARVED
// WORK CENTRES (frame L939): a starved station is a line about to stop for
// want of a pod, which is an andon in everything but the raising, so it lives
// here rather than on Quality.
//
// WITHOUT THE ANDON PREVIEW the tab is the same place with the app's older
// reads behind it: the operator's cards open the read-only sheet rather than
// a thread, so they carry no Reply; the leader's andons come from the shop
// floor's own unacknowledged list, worst first, with the "Ack ↗" link out to
// the Andon app that owns the clock. The starved work centres are the same
// either way.
//
// The title's sub-line is the frame's `alertsSub`: "Open on <scope> · N
// andons open floor-wide" for a leader, "Open at <work centre>" for an
// operator.

import { useMemo, useState } from 'react';
import type { AndonAlertVM } from '@/api/andon/types';
import type { AndonVM } from '@/api/types';
import { FloorCard } from '@/components/andon/FloorCard';
import { TakeSheet } from '@/components/andon/TakeSheet';
import { type NeedCardVM, NeedCardList } from '@/components/home/NeedCard';
import { EmptyState, Skeleton } from '@/components/ui/misc';
import { useTick } from '@/hooks/useTick';
import { useT } from '@/i18n/useT';
import { useAndonOpenCount, useFlowlineShort, useStationAndons } from '@/queries';
import { useAndonFloor } from '@/queries/andon';
import { andonNeedCard, useFloorNeeds } from '@/queries/leader';
import { useStore } from '@/store';
import type { NeedActions } from './HomeScreen';

export function AndonScreen({
  floor,
  flowlineIds,
  scopeLabel,
  actions,
}: {
  floor: boolean;
  flowlineIds: number[];
  scopeLabel: string;
  actions: NeedActions;
}) {
  const t = useT();
  const wcName = useStore((s) => s.workcentreName);
  const isLeader = useStore((s) => s.isLeader);
  const workcentreId = useStore((s) => s.workcentreId);
  const andonAppUrl = useStore((s) => s.andonAppUrl);
  const andonPreview = useStore((s) => s.andonCaps.preview);
  const short = useFlowlineShort();

  const station = useStationAndons(!floor);
  const openAndons = useAndonOpenCount();
  const andons = station.data ?? [];

  // The operator's card: the shop floor's own, with Reply as the verb into
  // the thread — the one thing a list can offer an operator honestly. Without
  // the preview a tap opens the read-only sheet, which nobody can reply in,
  // so the card is left as it comes.
  const toCard = useMemo(
    () =>
      (a: AndonVM): NeedCardVM => {
        const card = andonNeedCard(a, t, andonAppUrl, short(a.flowlineId), actions, isLeader, workcentreId, andonPreview);
        return andonPreview ? { ...card, verb: { label: t.replyT, onClick: () => actions.openAndon(a.id) } } : card;
      },
    [t, andonAppUrl, short, actions, isLeader, workcentreId, andonPreview],
  );

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
      <div style={{ display: 'flex', alignItems: 'baseline', gap: 8 }}>
        <h1 style={{ font: "700 18px 'Space Grotesk',sans-serif", margin: 0 }}>{t.andonsT}</h1>
        <span style={{ font: '500 12px Manrope,sans-serif', color: 'var(--sub)', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
          {floor ? `${t.openOnScopeT} ${scopeLabel} · ${openAndons.data ?? '—'} ${t.andonsOpenFloorWideT}` : `${t.openAtWcT} ${wcName ?? ''}`}
        </span>
      </div>

      {floor ? (
        <LeaderGroups flowlineIds={flowlineIds} actions={actions} andonPreview={andonPreview} />
      ) : (
        <Group label={`${t.andonsT} · ${andons.length}`} sub={andonPreview ? t.andonsAtYourWcT : t.andonsAtYourWcReadT}>
          <NeedCardList items={andons.map(toCard)} isPending={station.isPending} emptyText={t.noOpenAndonsT} compact />
        </Group>
      )}
    </div>
  );
}

/**
 * The leader's groups: the andons, then the starved work centres (frame
 * L939).
 *
 * The andons are the module's floor read with the preview on. Without it
 * they are the shop floor's own unacknowledged list, as the cards the Alerts
 * tab used to carry — "Ack ↗" out to the Andon app on each — because that
 * read needs no module and the link is still the honest verb while the
 * Andon app owns the clock.
 */
function LeaderGroups({ flowlineIds, actions, andonPreview }: { flowlineIds: number[]; actions: NeedActions; andonPreview: boolean }) {
  const t = useT();
  const floorNeeds = useFloorNeeds(flowlineIds, actions);
  return (
    <>
      {andonPreview ? (
        <LeaderFloor onOpen={actions.openAndon} />
      ) : (
        <Group label={`${t.andonsT} · ${floorNeeds.andons.length}`} sub={t.unansweredWorstFirstT}>
          <NeedCardList items={floorNeeds.items.filter((c) => c.key.startsWith('andon:'))} isPending={floorNeeds.isPending} emptyText={t.everyAndonAnsweredT} compact />
        </Group>
      )}
      <Group label={`${t.starvedWorkCentresT} · ${floorNeeds.starved.length}`} sub={t.nothingReadyWorkWaitingT}>
        <NeedCardList items={floorNeeds.items.filter((c) => c.key.startsWith('starved:'))} isPending={floorNeeds.isPending} emptyText={t.nothingWrongT} compact />
      </Group>
    </>
  );
}

/**
 * The leader's two groups, from the andon module's own floor read.
 *
 * "Take it" opens the ETA sheet for that card without leaving the list — a
 * leader clearing the Nobody-yet group takes three andons in three sheets
 * rather than three threads — and the take lands exactly as it does from the
 * thread: one call, a time on the record, the operator told.
 */
function LeaderFloor({ onOpen }: { onOpen: (andonId: number) => void }) {
  const t = useT();
  const now = useTick();
  const connMode = useStore((s) => s.connMode);
  const floor = useAndonFloor('open', true);
  const alerts = floor.data?.alerts ?? [];
  const [taking, setTaking] = useState<AndonAlertVM | null>(null);

  // Taking writes an Odoo user onto the record; a PIN session is the service
  // account and names nobody, so the card carries no verb there.
  const canTake = connMode === 'user';
  const nobodyYet = alerts.filter((a) => a.open && a.stage === 'new');
  const responding = alerts.filter((a) => a.open && a.stage !== 'new');

  if (!floor.isPending && alerts.length === 0) {
    return (
      <EmptyState>
        <div style={{ font: "700 15px 'Space Grotesk',sans-serif", color: 'var(--ink)', marginBottom: 6 }}>{t.noOpenAndonsT}</div>
        {t.everyAndonAnsweredT}
      </EmptyState>
    );
  }

  const cards = (list: AndonAlertVM[], empty: string) =>
    floor.isPending && list.length === 0 ? (
      <div style={{ display: 'grid', gap: 8 }}>
        <Skeleton height={74} radius={15} />
        <Skeleton height={74} radius={15} />
      </div>
    ) : list.length === 0 ? (
      <div style={{ background: 'var(--surface2)', borderRadius: 13 }}>
        <EmptyState style={{ padding: 16 }}>{empty}</EmptyState>
      </div>
    ) : (
      <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
        {list.map((a) => (
          <FloorCard key={a.id} alert={a} now={now} onOpen={() => onOpen(a.id)} onTake={canTake ? () => setTaking(a) : undefined} />
        ))}
      </div>
    );

  return (
    <>
      <Group label={`${t.nobodyYetT} · ${nobodyYet.length}`} sub={t.waitingForTakerT}>
        {cards(nobodyYet, t.everyAndonAnsweredT)}
      </Group>
      <Group label={`${t.respondingTagT} · ${responding.length}`} sub={t.somebodyOnItT}>
        {cards(responding, t.noOpenAndonsT)}
      </Group>
      <TakeSheet target={taking} onClose={() => setTaking(null)} onOpenThread={onOpen} />
    </>
  );
}

function Group({ label, sub, children }: { label: string; sub: string; children: React.ReactNode }) {
  return (
    <section aria-label={label}>
      <div className="sectionLabel" style={{ marginBottom: 4 }}>
        {label}
      </div>
      <div style={{ font: '500 11px Manrope,sans-serif', color: 'var(--sub)', marginBottom: 8 }}>{sub}</div>
      {children}
    </section>
  );
}
